import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { 
  generateCostComparisonForPDF,
  generateAllowancesComparisonForPDF,
  generateContractorScoresForPDF,
  generateMissingElementsForPDF,
  generateRiskAssessmentForPDF,
  generateKeyInsightsForPDF
} from '../../utils/chartToPDFUtils';

// Register fonts for better typography (with fallbacks)
try {
  Font.register({
    family: 'Inter',
    fonts: [
      { src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyeMZhrib2Bg-4.woff2', fontWeight: 400 },
      { src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYeZhrib2Bg-4.woff2', fontWeight: 600 },
      { src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYeZhrib2Bg-4.woff2', fontWeight: 700 },
    ]
  });
} catch (fontError) {
  console.warn('Font registration failed, using system fonts:', fontError);
}

// Define professional styles based on Trakvu's color scheme
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    fontFamily: 'Inter, Helvetica, Arial, sans-serif',
    fontSize: 11,
    color: '#1e293b',
    lineHeight: 1.4,
  },
  
  // Header styles
  header: {
    backgroundColor: '#0077cc',
    padding: 30,
    color: 'white',
    marginBottom: 0,
  },
  
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  
  brandSection: {
    flexDirection: 'column',
  },
  
  brandName: {
    fontSize: 32,
    fontWeight: 700,
    color: 'white',
    marginBottom: 4,
  },
  
  brandTagline: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    fontWeight: 400,
  },
  
  reportInfo: {
    alignItems: 'flex-end',
  },
  
  reportTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 4,
  },
  
  reportDate: {
    fontSize: 10,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  
  headerContent: {
    flexDirection: 'column',
  },
  
  projectTitle: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 8,
    color: 'white',
  },
  
  projectSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: 400,
  },
  
  // Content container
  content: {
    padding: 30,
    flex: 1,
  },
  
  // Section styles
  section: {
    marginBottom: 30,
  },
  
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#0077cc',
    marginBottom: 15,
    borderBottomWidth: 2,
    borderBottomColor: '#e2e8f0',
    paddingBottom: 8,
  },
  
  // Score bar styles for visual performance indicators
  scoreSection: {
    marginBottom: 25,
  },
  
  scoreItem: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: '#f8fafc',
    borderRadius: 4,
    border: '1px solid #e2e8f0',
  },
  
  scoreHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  
  contractorName: {
    fontSize: 12,
    fontWeight: 600,
    color: '#1e293b',
  },
  
  scoreValue: {
    fontSize: 11,
    color: '#64748b',
    fontWeight: 500,
  },
  
  progressBarContainer: {
    marginBottom: 6,
  },
  
  progressBarLabel: {
    fontSize: 9,
    color: '#475569',
    marginBottom: 3,
  },
  
  progressBarTrack: {
    width: '100%',
    height: 8,
    backgroundColor: '#e2e8f0',
    borderRadius: 4,
    overflow: 'hidden',
  },
  
  progressBarFill: {
    height: '100%',
    borderRadius: 4,
  },
  
  ratingBadge: {
    marginTop: 5,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  
  ratingText: {
    fontSize: 9,
    fontWeight: 600,
  },
  
  sectionSubtitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#64748b',
    marginBottom: 12,
  },
  
  // Executive Summary styles
  executiveSummary: {
    backgroundColor: '#f8fafc',
    padding: 20,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#0077cc',
    marginBottom: 25,
  },
  
  summaryTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#0077cc',
    marginBottom: 10,
  },
  
  summaryText: {
    fontSize: 12,
    lineHeight: 1.5,
    color: '#475569',
    marginBottom: 8,
  },
  
  // Recommendation styles
  recommendationCard: {
    backgroundColor: '#0077cc',
    color: 'white',
    padding: 20,
    borderRadius: 8,
    marginBottom: 20,
  },
  
  recommendationTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  
  recommendationContractor: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 8,
  },
  
  recommendationReason: {
    fontSize: 12,
    lineHeight: 1.4,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  
  // Table styles
  table: {
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 6,
    marginBottom: 20,
  },
  
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f8fafc',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
    minHeight: 35,
  },
  
  tableCell: {
    flex: 1,
    padding: 10,
    fontSize: 10,
    justifyContent: 'center',
  },
  
  tableCellHeader: {
    flex: 1,
    padding: 10,
    fontSize: 11,
    fontWeight: 600,
    color: '#475569',
    justifyContent: 'center',
  },
  
  // Contractor card styles
  contractorCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
  },
  
  contractorHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  contractorName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1e293b',
  },
  
  contractorPrice: {
    fontSize: 16,
    fontWeight: 700,
    color: '#0077cc',
  },
  
  contractorDetails: {
    fontSize: 10,
    color: '#64748b',
    lineHeight: 1.4,
  },
  
  // Key metrics styles
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  
  metricCard: {
    width: '48%',
    backgroundColor: '#f8fafc',
    padding: 15,
    borderRadius: 6,
    marginBottom: 10,
  },
  
  metricTitle: {
    fontSize: 10,
    color: '#64748b',
    fontWeight: 500,
    marginBottom: 4,
  },
  
  metricValue: {
    fontSize: 18,
    fontWeight: 700,
    color: '#0077cc',
  },
  
  metricSubtext: {
    fontSize: 9,
    color: '#94a3b8',
    marginTop: 2,
  },
  
  // Analysis styles
  analysisItem: {
    flexDirection: 'row',
    marginBottom: 8,
    alignItems: 'center',
  },
  
  analysisIcon: {
    width: 12,
    height: 12,
    backgroundColor: '#0077cc',
    borderRadius: 6,
    marginRight: 8,
  },
  
  analysisText: {
    fontSize: 11,
    color: '#475569',
    flex: 1,
  },
  
  // Chart placeholder styles
  chartPlaceholder: {
    height: 200,
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  
  chartTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#64748b',
    marginBottom: 10,
    textAlign: 'center',
  },
  
  // Text chart styles
  textChart: {
    backgroundColor: '#f8fafc',
    padding: 15,
    borderRadius: 6,
    marginBottom: 15,
  },
  
  chartRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    paddingVertical: 4,
  },
  
  chartLabel: {
    width: '30%',
    fontSize: 10,
    fontWeight: 500,
    color: '#475569',
  },
  
  chartBar: {
    width: '40%',
    fontSize: 10,
    fontFamily: 'Courier',
    color: '#0077cc',
    marginHorizontal: 8,
  },
  
  chartValue: {
    width: '30%',
    fontSize: 10,
    fontWeight: 600,
    color: '#1e293b',
    textAlign: 'right',
  },
  
  chartHeader: {
    flexDirection: 'row',
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  
  chartHeaderText: {
    fontSize: 10,
    fontWeight: 600,
    color: '#64748b',
  },
  
  // Footer styles
  footer: {
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 20,
    paddingHorizontal: 30,
    paddingBottom: 20,
    backgroundColor: '#f8fafc',
  },
  
  footerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  
  footerText: {
    fontSize: 9,
    color: '#64748b',
  },
  
  footerBrand: {
    fontSize: 10,
    fontWeight: 600,
    color: '#0077cc',
  },
  
  // Utility styles
  bold: {
    fontWeight: 600,
  },
  
  center: {
    textAlign: 'center',
  },
  
  spacerSmall: {
    height: 10,
  },
  
  spacerMedium: {
    height: 20,
  },
  
  spacerLarge: {
    height: 30,
  },
  
  // Risk and warning styles
  warningBox: {
    backgroundColor: '#fef3cd',
    border: '1px solid #f59e0b',
    borderRadius: 6,
    padding: 12,
    marginBottom: 15,
  },
  
  warningTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#92400e',
    marginBottom: 4,
  },
  
  warningText: {
    fontSize: 10,
    color: '#92400e',
    lineHeight: 1.3,
  },
  
  successBox: {
    backgroundColor: '#dcfce7',
    border: '1px solid #10b981',
    borderRadius: 6,
    padding: 12,
    marginBottom: 15,
  },
  
  successTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#059669',
    marginBottom: 4,
  },
  
  successText: {
    fontSize: 10,
    color: '#059669',
    lineHeight: 1.3,
  },
});

const PDFReportGenerator = ({ 
  recommendationData, 
  evaluationData, 
  consolidatedData,
  guestAnalysisData,
  userPlan = 'free',
  hasActiveSubscription = false 
}) => {
  
  // Check if we're using guest analysis data
  const isUsingGuestData = guestAnalysisData && (!consolidatedData || userPlan === 'free');
  
  // Transform guest data to expected format
  const transformGuestData = () => {
    if (!guestAnalysisData) {
      return { contractors: [], recommendation: null };
    }
    
    const actualData = guestAnalysisData.guestAnalysis || guestAnalysisData;
    const contractors = [];
    
    // Transform Contractor_1_Information
    if (actualData.Contractor_1_Information) {
      const c1 = actualData.Contractor_1_Information;
      contractors.push({
        contractor: {
          contractor_name: c1.Company_Name || 'Contractor 1'
        },
        payment_terms: {
          total_cost: c1.Financials?.Base_Price?.amount || c1.Financials?.total_cost || '0'
        },
        project_scope: c1.Scope?.Included_Work_Scope || c1.project_scope || 'Scope not specified',
        exclusions: c1.Scope?.Exclusions || c1.exclusions || [],
        allowances: {
          direct_costs: c1.Financials?.Cost_Breakdown?.allowances?.direct || c1.allowances?.direct_costs || {}
        }
      });
    }
    
    // Transform Contractor_2_Information
    if (actualData.Contractor_2_Information) {
      const c2 = actualData.Contractor_2_Information;
      contractors.push({
        contractor: {
          contractor_name: c2.Company_Name || 'Contractor 2'
        },
        payment_terms: {
          total_cost: c2.Financials?.Base_Price?.amount || c2.Financials?.total_cost || '0'
        },
        project_scope: c2.Scope?.Included_Work_Scope || c2.project_scope || 'Scope not specified',
        exclusions: c2.Scope?.Exclusions || c2.exclusions || [],
        allowances: {
          direct_costs: c2.Financials?.Cost_Breakdown?.allowances?.direct || c2.allowances?.direct_costs || {}
        }
      });
    }
    
    // Transform recommendation data
    const recommendation = actualData.comparision?.Recommendation ? {
      recommended_contractor: actualData.comparision.Recommendation.Preferred_Contractor,
      reasoning: actualData.comparision.Recommendation.Reason,
      ranking: [
        {
          contractor_name: contractors[0]?.contractor?.contractor_name || 'Contractor 1',
          alignment_score: actualData.comparision?.Contractor_1_Score?.Total || 0
        },
        {
          contractor_name: contractors[1]?.contractor?.contractor_name || 'Contractor 2', 
          alignment_score: actualData.comparision?.Contractor_2_Score?.Total || 0
        }
      ]
    } : null;
    
    return { contractors, recommendation };
  };
  
  // Extract contractor data
  const contractors = (() => {
    if (isUsingGuestData) {
      const transformedData = transformGuestData();
      return transformedData.contractors;
    }
    
    return consolidatedData ? 
      [1, 2, 3, 4, 5]
        .map(num => consolidatedData[`contractor_${num}`])
        .filter(Boolean) 
      : [];
  })();

  // Get recommendation data
  const recommendation = (() => {
    if (isUsingGuestData) {
      const transformedData = transformGuestData();
      return transformedData.recommendation;
    }
    return recommendationData;
  })();

  // Calculate key metrics
  const calculateMetrics = () => {
    const costs = contractors.map(contractor => {
      const costStr = contractor.payment_terms?.total_cost?.toString() || '0';
      const cleanCost = costStr.replace(/[\$,]/g, '');
      return parseFloat(cleanCost) || 0;
    });

    const minCost = Math.min(...costs);
    const maxCost = Math.max(...costs);
    const avgCost = costs.reduce((sum, cost) => sum + cost, 0) / costs.length;
    const costDifference = maxCost - minCost;
    const costDifferencePercentage = ((costDifference / minCost) * 100).toFixed(1);

    return {
      contractorsEvaluated: contractors.length,
      costRange: `$${minCost.toLocaleString()} - $${maxCost.toLocaleString()}`,
      averageCost: avgCost,
      maxSavings: costDifference,
      costDifferencePercentage,
      recommendedContractor: recommendation?.recommended_contractor || 'Not available'
    };
  };

  const metrics = calculateMetrics();
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Generate chart data for PDF
  const costChartData = generateCostComparisonForPDF(contractors);
  const allowancesData = generateAllowancesComparisonForPDF(contractors);
  const contractorScores = generateContractorScoresForPDF(
    contractors, 
    evaluationData, 
    recommendation, 
    isUsingGuestData, 
    guestAnalysisData
  );
  const missingElementsData = generateMissingElementsForPDF(
    contractors, 
    evaluationData, 
    isUsingGuestData, 
    guestAnalysisData
  );
  const riskAssessment = generateRiskAssessmentForPDF(contractors, metrics);
  const keyInsights = generateKeyInsightsForPDF(contractors, metrics, recommendation);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerTop}>
            <View style={styles.brandSection}>
              <Text style={styles.brandName}>Trakvu</Text>
              <Text style={styles.brandTagline}>Smart Construction Technology</Text>
            </View>
            <View style={styles.reportInfo}>
              <Text style={styles.reportTitle}>Proposal Analysis Report</Text>
              <Text style={styles.reportDate}>{currentDate}</Text>
            </View>
          </View>
          <View style={styles.headerContent}>
            <Text style={styles.projectTitle}>Contractor Proposal Comparison</Text>
            <Text style={styles.projectSubtitle}>
              AI-Powered Analysis of {contractors.length} Contractor Proposals
            </Text>
          </View>
        </View>

        {/* Content */}
        <View style={styles.content}>
          {/* Executive Summary */}
          <View style={styles.executiveSummary}>
            <Text style={styles.summaryTitle}>Executive Summary</Text>
            <Text style={styles.summaryText}>
              Our AI analysis evaluated {contractors.length} contractor proposals for your project. 
              The cost difference between proposals is ${metrics.maxSavings.toLocaleString()} ({metrics.costDifferencePercentage}%), 
              with an average project cost of ${metrics.averageCost.toLocaleString()}.
            </Text>
            <Text style={styles.summaryText}>
              {recommendation ? 
                `Based on comprehensive analysis, we recommend ${recommendation.recommended_contractor} as the optimal choice for your project.` :
                'Detailed recommendations are available with upgraded analysis plans.'
              }
            </Text>
          </View>

          {/* Key Metrics */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Key Metrics</Text>
            <View style={styles.metricsGrid}>
              <View style={styles.metricCard}>
                <Text style={styles.metricTitle}>Contractors Evaluated</Text>
                <Text style={styles.metricValue}>{metrics.contractorsEvaluated}</Text>
                <Text style={styles.metricSubtext}>Comprehensive analysis</Text>
              </View>
              <View style={styles.metricCard}>
                <Text style={styles.metricTitle}>Cost Range</Text>
                <Text style={styles.metricValue}>{metrics.costRange}</Text>
                <Text style={styles.metricSubtext}>{metrics.costDifferencePercentage}% difference</Text>
              </View>
              <View style={styles.metricCard}>
                <Text style={styles.metricTitle}>Average Cost</Text>
                <Text style={styles.metricValue}>${metrics.averageCost.toLocaleString()}</Text>
                <Text style={styles.metricSubtext}>Market benchmark</Text>
              </View>
              <View style={styles.metricCard}>
                <Text style={styles.metricTitle}>Potential Savings</Text>
                <Text style={styles.metricValue}>${metrics.maxSavings.toLocaleString()}</Text>
                <Text style={styles.metricSubtext}>By choosing wisely</Text>
              </View>
            </View>
          </View>

          {/* Recommendation */}
          {recommendation && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>AI Recommendation</Text>
              <View style={styles.recommendationCard}>
                <Text style={styles.recommendationTitle}>🏆 Recommended Contractor</Text>
                <Text style={styles.recommendationContractor}>{recommendation.recommended_contractor}</Text>
                <Text style={styles.recommendationReason}>
                  {recommendation.reasoning || 'Based on comprehensive analysis of cost, quality, timeline, and alignment with your requirements.'}
                </Text>
              </View>
            </View>
          )}

          {/* Contractor Comparison */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Contractor Comparison</Text>
            
            {/* Cost Comparison Chart with Visual Bars */}
            <Text style={styles.sectionSubtitle}>Cost Analysis</Text>
            {costChartData.length > 0 && (
              <View style={styles.scoreSection}>
                {costChartData.map((item, index) => {
                  // Calculate percentage for visual bar (higher cost = longer bar)
                  const maxCost = Math.max(...costChartData.map(c => c.value));
                  const costPercentage = maxCost > 0 ? (item.value / maxCost) * 100 : 0;
                  
                  // Color coding: green for lowest cost, red for highest
                  const minCost = Math.min(...costChartData.map(c => c.value));
                  const isLowest = item.value === minCost;
                  const isHighest = item.value === maxCost;
                  const barColor = isLowest ? '#10b981' : isHighest ? '#ef4444' : '#f59e0b';
                  
                  return (
                    <View key={index} style={styles.scoreItem}>
                      <View style={styles.scoreHeader}>
                        <Text style={styles.contractorName}>{item.name}</Text>
                        <Text style={styles.scoreValue}>{item.formattedValue}</Text>
                      </View>
                      
                      <View style={styles.progressBarContainer}>
                        <Text style={styles.progressBarLabel}>
                          Relative Cost: {costPercentage.toFixed(0)}% of highest bid
                          {isLowest && ' (LOWEST)'}
                          {isHighest && ' (HIGHEST)'}
                        </Text>
                        <View style={styles.progressBarTrack}>
                          <View 
                            style={[
                              styles.progressBarFill, 
                              { 
                                width: `${costPercentage}%`, 
                                backgroundColor: barColor 
                              }
                            ]} 
                          />
                        </View>
                      </View>
                    </View>
                  );
                })}
              </View>
            )}

            {/* Cost Comparison Table */}
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Contractor</Text>
                <Text style={styles.tableCellHeader}>Total Cost</Text>
                <Text style={styles.tableCellHeader}>vs Average</Text>
                <Text style={styles.tableCellHeader}>Market Position</Text>
              </View>
              {contractors.map((contractor, index) => {
                const costStr = contractor.payment_terms?.total_cost?.toString() || '0';
                const cost = parseFloat(costStr.replace(/[\$,]/g, '')) || 0;
                const vsAverage = ((cost - metrics.averageCost) / metrics.averageCost * 100).toFixed(1);
                const isHigher = cost > metrics.averageCost;
                
                return (
                  <View key={index} style={styles.tableRow}>
                    <Text style={styles.tableCell}>{contractor.contractor.contractor_name}</Text>
                    <Text style={styles.tableCell}>${cost.toLocaleString()}</Text>
                    <Text style={styles.tableCell}>
                      {isHigher ? '+' : ''}{vsAverage}%
                    </Text>
                    <Text style={styles.tableCell}>
                      {cost === Math.min(...contractors.map(c => parseFloat(c.payment_terms?.total_cost?.toString().replace(/[\$,]/g, '') || '0'))) ? 
                        'Most Competitive' : 
                        cost === Math.max(...contractors.map(c => parseFloat(c.payment_terms?.total_cost?.toString().replace(/[\$,]/g, '') || '0'))) ? 
                        'Premium Pricing' : 
                        'Market Average'
                      }
                    </Text>
                  </View>
                );
              })}
            </View>

            {/* Contractor Quality Scores with Visual Bars */}
            {contractorScores.length > 0 && (
              <View style={styles.scoreSection}>
                <Text style={styles.sectionSubtitle}>Quality & Performance Scores</Text>
                {contractorScores.map((score, index) => {
                  // Calculate score percentages for visual bars
                  const qualityPercentage = (score.overallScore / 10) * 100;
                  const alignmentPercentage = score.alignmentScore;
                  
                  // Determine colors based on score ranges
                  const getScoreColor = (percentage) => {
                    if (percentage >= 80) return '#10b981'; // Green
                    if (percentage >= 60) return '#f59e0b'; // Orange
                    return '#ef4444'; // Red
                  };
                  
                  const getRatingBadgeColor = (rating) => {
                    switch (rating) {
                      case 'Excellent': return { bg: '#dcfce7', text: '#166534' };
                      case 'Good': return { bg: '#fef3c7', text: '#92400e' };
                      case 'Fair': return { bg: '#fed7aa', text: '#9a3412' };
                      default: return { bg: '#fee2e2', text: '#991b1b' };
                    }
                  };
                  
                  const qualityColor = getScoreColor(qualityPercentage);
                  const alignmentColor = getScoreColor(alignmentPercentage);
                  const badgeColors = getRatingBadgeColor(score.qualityRating);
                  
                  return (
                    <View key={index} style={styles.scoreItem}>
                      <View style={styles.scoreHeader}>
                        <Text style={styles.contractorName}>{score.name}</Text>
                        <View style={[styles.ratingBadge, { backgroundColor: badgeColors.bg }]}>
                          <Text style={[styles.ratingText, { color: badgeColors.text }]}>
                            {score.qualityRating}
                          </Text>
                        </View>
                      </View>
                      
                      <View style={styles.progressBarContainer}>
                        <Text style={styles.progressBarLabel}>
                          Quality Score: {score.overallScore}/10 ({qualityPercentage.toFixed(0)}%)
                        </Text>
                        <View style={styles.progressBarTrack}>
                          <View 
                            style={[
                              styles.progressBarFill, 
                              { 
                                width: `${qualityPercentage}%`, 
                                backgroundColor: qualityColor 
                              }
                            ]} 
                          />
                        </View>
                      </View>
                      
                      <View style={styles.progressBarContainer}>
                        <Text style={styles.progressBarLabel}>
                          Alignment Score: {alignmentPercentage}%
                        </Text>
                        <View style={styles.progressBarTrack}>
                          <View 
                            style={[
                              styles.progressBarFill, 
                              { 
                                width: `${alignmentPercentage}%`, 
                                backgroundColor: alignmentColor 
                              }
                            ]} 
                          />
                        </View>
                      </View>
                    </View>
                  );
                })}
              </View>
            )}
          </View>
        </View>
      </Page>

      {/* Page 2 - Detailed Analysis */}
      <Page size="A4" style={styles.page}>
        <View style={styles.content}>
          {/* Detailed Contractor Profiles */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Detailed Contractor Profiles</Text>
            
            {contractors.map((contractor, index) => (
              <View key={index} style={styles.contractorCard}>
                <View style={styles.contractorHeader}>
                  <Text style={styles.contractorName}>
                    {contractor.contractor.contractor_name}
                  </Text>
                  <Text style={styles.contractorPrice}>
                    ${parseFloat(contractor.payment_terms?.total_cost?.toString().replace(/[\$,]/g, '') || '0').toLocaleString()}
                  </Text>
                </View>
                
                <Text style={styles.contractorDetails}>
                  <Text style={styles.bold}>Project Scope: </Text>
                  {contractor.project_scope.substring(0, 200)}...
                </Text>
                
                {contractor.exclusions && contractor.exclusions.length > 0 && (
                  <Text style={styles.contractorDetails}>
                    <Text style={styles.bold}>Exclusions: </Text>
                    {contractor.exclusions.slice(0, 2).join(', ')}
                    {contractor.exclusions.length > 2 && ` and ${contractor.exclusions.length - 2} more`}
                  </Text>
                )}
                
                {/* Allowances Summary */}
                {contractor.allowances?.direct_costs && Object.keys(contractor.allowances.direct_costs).length > 0 && (
                  <Text style={styles.contractorDetails}>
                    <Text style={styles.bold}>Key Allowances: </Text>
                    {Object.entries(contractor.allowances.direct_costs)
                      .slice(0, 3)
                      .map(([key, value]) => `${key.replace(/_/g, ' ')}: $${value}`)
                      .join(', ')}
                  </Text>
                )}
              </View>
            ))}
          </View>

          {/* Missing Elements Analysis */}
          {missingElementsData.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Missing Elements Analysis</Text>
              <View style={styles.table}>
                <View style={styles.tableHeader}>
                  <Text style={styles.tableCellHeader}>Contractor</Text>
                  <Text style={styles.tableCellHeader}>Missing Items</Text>
                  <Text style={styles.tableCellHeader}>Coverage Level</Text>
                  <Text style={styles.tableCellHeader}>Risk Level</Text>
                </View>
                {missingElementsData.map((item, index) => (
                  <View key={index} style={styles.tableRow}>
                    <Text style={styles.tableCell}>{item.name}</Text>
                    <Text style={styles.tableCell}>{item.missing}</Text>
                    <Text style={styles.tableCell}>{item.coverage}</Text>
                    <Text style={styles.tableCell}>
                      {item.missing > 3 ? 'High' : item.missing > 1 ? 'Medium' : 'Low'}
                    </Text>
                  </View>
                ))}
              </View>
              {missingElementsData.some(item => item.items && item.items.length > 0) && (
                <View style={styles.warningBox}>
                  <Text style={styles.warningTitle}>Common Missing Elements</Text>
                  {missingElementsData.filter(item => item.items && item.items.length > 0).map((item, index) => (
                    <Text key={index} style={styles.warningText}>
                      <Text style={styles.bold}>{item.name}:</Text> {item.items.join(', ')}
                    </Text>
                  ))}
                </View>
              )}
            </View>
          )}

          {/* Allowances Breakdown */}
          {allowancesData.totals && allowancesData.totals.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Allowances Analysis</Text>
              
              <Text style={styles.sectionSubtitle}>Total Allowances Comparison</Text>
              <View style={styles.scoreSection}>
                {allowancesData.totals.map((item, index) => {
                  // Calculate percentage for visual bar
                  const maxAllowance = Math.max(...allowancesData.totals.map(a => a.value));
                  const allowancePercentage = maxAllowance > 0 ? (item.value / maxAllowance) * 100 : 0;
                  
                  // Color coding: green for lower allowances, orange for higher
                  const minAllowance = Math.min(...allowancesData.totals.map(a => a.value));
                  const isLowest = item.value === minAllowance;
                  const isHighest = item.value === maxAllowance;
                  const barColor = isLowest ? '#10b981' : isHighest ? '#f59e0b' : '#0077cc';
                  
                  return (
                    <View key={index} style={styles.scoreItem}>
                      <View style={styles.scoreHeader}>
                        <Text style={styles.contractorName}>{item.name}</Text>
                        <Text style={styles.scoreValue}>{item.formattedValue}</Text>
                      </View>
                      
                      <View style={styles.progressBarContainer}>
                        <Text style={styles.progressBarLabel}>
                          Allowances: {allowancePercentage.toFixed(0)}% of highest allowance
                          {isLowest && ' (LOWEST)'}
                          {isHighest && ' (HIGHEST)'}
                        </Text>
                        <View style={styles.progressBarTrack}>
                          <View 
                            style={[
                              styles.progressBarFill, 
                              { 
                                width: `${allowancePercentage}%`, 
                                backgroundColor: barColor 
                              }
                            ]} 
                          />
                        </View>
                      </View>
                    </View>
                  );
                })}
              </View>

              {/* Top Categories */}
              {allowancesData.categories && allowancesData.categories.length > 0 && (
                <View>
                  <Text style={styles.sectionSubtitle}>Top Allowance Categories</Text>
                  {allowancesData.categories.slice(0, 3).map((category, index) => (
                    <View key={index} style={styles.contractorCard}>
                      <Text style={styles.contractorName}>{category.category}</Text>
                      <View style={styles.textChart}>
                        {category.chart.map((item, chartIndex) => (
                          <View key={chartIndex} style={styles.chartRow}>
                            <Text style={styles.chartLabel}>{item.name}</Text>
                            <Text style={styles.chartBar}>{item.bar}</Text>
                            <Text style={styles.chartValue}>{item.formattedValue}</Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </View>
          )}

          {/* Risk Analysis */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Risk Assessment</Text>
            
            {riskAssessment.map((risk, index) => {
              const isHighRisk = risk.level === 'High';
              const isMediumRisk = risk.level === 'Medium';
              
              return (
                <View key={index} style={isHighRisk ? styles.warningBox : isMediumRisk ? styles.warningBox : styles.successBox}>
                  <Text style={isHighRisk ? styles.warningTitle : isMediumRisk ? styles.warningTitle : styles.successTitle}>
                    {risk.type} - {risk.level} Risk
                  </Text>
                  <Text style={isHighRisk ? styles.warningText : isMediumRisk ? styles.warningText : styles.successText}>
                    <Text style={styles.bold}>Analysis:</Text> {risk.description}
                  </Text>
                  <Text style={isHighRisk ? styles.warningText : isMediumRisk ? styles.warningText : styles.successText}>
                    <Text style={styles.bold}>Recommendation:</Text> {risk.recommendation}
                  </Text>
                </View>
              );
            })}

            {/* Recommendation Confidence */}
            {recommendation && (
              <View style={styles.successBox}>
                <Text style={styles.successTitle}>AI Recommendation Confidence</Text>
                <Text style={styles.successText}>
                  Our AI analysis has high confidence in recommending {recommendation.recommended_contractor} 
                  based on cost-effectiveness, scope completeness, and alignment with your project requirements.
                </Text>
              </View>
            )}
          </View>

          {/* Key Insights */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Key Insights & Recommendations</Text>
            
            {keyInsights.map((insight, index) => (
              <View key={index} style={styles.analysisItem}>
                <View style={styles.analysisIcon} />
                <View style={{ flex: 1 }}>
                  <Text style={[styles.analysisText, styles.bold]}>
                    {insight.category}: {insight.title}
                  </Text>
                  <Text style={styles.analysisText}>
                    {insight.description}
                  </Text>
                  <Text style={[styles.analysisText, { fontSize: 9, color: '#94a3b8', marginTop: 2 }]}>
                    Impact: {insight.impact}
                  </Text>
                </View>
              </View>
            ))}
            
            <View style={styles.analysisItem}>
              <View style={styles.analysisIcon} />
              <Text style={styles.analysisText}>
                <Text style={styles.bold}>Next Steps:</Text> Request clarifications on scope differences, 
                verify contractor qualifications, and finalize contractor selection based on your priorities
              </Text>
            </View>
          </View>

          {/* Plan Information */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Analysis Plan Information</Text>
            <Text style={styles.contractorDetails}>
              <Text style={styles.bold}>Current Plan: </Text>
              {userPlan.charAt(0).toUpperCase() + userPlan.slice(1)} Plan
            </Text>
            <Text style={styles.contractorDetails}>
              <Text style={styles.bold}>Analysis Type: </Text>
              {isUsingGuestData ? 'Basic Analysis' : 'Comprehensive Analysis'}
            </Text>
            {userPlan === 'free' && (
              <Text style={styles.contractorDetails}>
                <Text style={styles.bold}>Upgrade Benefits: </Text>
                Unlock detailed contractor scoring, risk analysis, and advanced comparisons with Essential, Complete, or Expert plans.
              </Text>
            )}
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <View style={styles.footerContent}>
            <Text style={styles.footerText}>
              Generated by Trakvu AI • {currentDate} • Confidential Report
            </Text>
            <Text style={styles.footerBrand}>
              Trakvu.com
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PDFReportGenerator; 